import { Component } from '@theme/component';
import { cartRequirements, cartSignature, lineRequirements, parseInventoryProperty, positiveInteger, shortages } from '@theme/fehma-stock-utils';

const THEME_ID = 193617953132;
const INVENTORY_URL = 'https://cdn-zeptoapps.com/product-personalizer/getvariants.php';
const ACTIVE_LINKS = '.pplr-wrapper input[data-variant]:not([disabled]), .pplr-wrapper select[data-variant]:not([disabled])';
const ADD_BUTTON = 'sticky-add-to-cart button, button[type="submit"], input[type="submit"], .p_a_t_c, .p_a_t_c_o, .shopify-payment-button__button';

/**
 * Window capture precedes Zepto's document-capture click handler. No fetch/XHR
 * patching, speculative cart adds, rollback, or transform rewriting.
 * @extends {Component}
 */
export class FehmaStockValidation extends Component {
  #controller;
  #pending = null;
  #replaying = null;

  connectedCallback() {
    super.connectedCallback();
    this.#controller?.abort();
    this.#controller = new AbortController();
    this.#pending = null;
    const { signal } = this.#controller;
    window.addEventListener('click', this.#onClick, { capture: true, signal });
    window.addEventListener('submit', this.#onSubmit, { capture: true, signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller?.abort();
    this.#pending = null;
  }

  /** Enabled submitted fields count; hidden conditional swatches may remain selected. */
  #selection(form) {
    const links = [];
    for (const field of document.querySelectorAll(ACTIVE_LINKS)) {
      if (field.form !== form || !field.name || !field.value) continue;
      if ((field.type === 'radio' || field.type === 'checkbox') && !field.checked) continue;
      if (field.closest('.pplr-wrapper')?.classList.contains('pplr_no_preview')) continue;
      for (const part of (field.getAttribute('data-variant') || '').split('||')) {
        if (!part) continue;
        const [id, , handle] = part.split(',');
        if (!/^\d+$/.test(id) || !handle || !/^[a-z0-9-]+$/i.test(handle)) throw new Error('Unknown linked variant');
        links.push({ id, handle, name: field.value, field: field.name });
      }
    }
    for (const field of form.elements) {
      if (!field.disabled && /^properties\[Select Charm \d+\]$/.test(field.name) && field.value &&
          !links.some((link) => link.field === field.name)) throw new Error('Selected charm has no inventory link');
    }
    const data = new FormData(form);
    const quantity = positiveInteger(data.get('quantity') || 1);
    const picker = this.#packagePicker(form);
    if (picker) {
      // These are FEHMA's existing merchant-authored package names and IDs.
      const count = Number(picker.value.match(/^(1|3|5) Charms?$/)?.[1]);
      const fields = [...form.elements].filter((field) =>
        !field.disabled && /^properties\[Select Charm \d+\]$/.test(field.name));
      if (!count || fields.length !== count ||
          fields.some((field, index) => field.name !== `properties[Select Charm ${index + 1}]`) ||
          picker.selectedOptions[0]?.dataset.variantId !== String(data.get('id'))) {
        throw new Error('Charm package is not ready');
      }
    }
    return { links, quantity, signature: JSON.stringify([data.get('id'), quantity, picker?.value, links]) };
  }

  #packagePicker(form) {
    return form.closest('.shopify-section')?.querySelector('variant-picker select[name="options[Charm Package]"]');
  }

  #formFor(button) {
    const form = button.form || button.closest('form');
    return form instanceof HTMLFormElement && (form.querySelector('.product-personalizer') || this.#packagePicker(form)) ? form : null;
  }

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    if (!(event.target instanceof Element)) return;
    let button = event.target.closest(ADD_BUTTON);
    if (!(button instanceof HTMLElement) || button === this.#replaying) return;
    // Validate the original form before the sticky handler can animate success.
    if (button.closest('sticky-add-to-cart')) {
      const original = button.closest('.shopify-section')?.querySelector('form[data-type="add-to-cart-form"], form:has(.product-personalizer)')?.querySelector('button[name="add"]');
      if (!(original instanceof HTMLButtonElement)) return;
      button = original;
    }
    const form = this.#formFor(button);
    if (form) this.#intercept(event, form, button);
  };

  /** Keyboard and requestSubmit paths enter Zepto through its normal click path. */
  #onSubmit = (event) => {
    if (!(event.target instanceof HTMLFormElement)) return;
    const form = event.target;
    if ((!form.querySelector('.product-personalizer') && !this.#packagePicker(form)) || this.#replaying) return;
    const button = event.submitter || form.querySelector(ADD_BUTTON);
    if (button instanceof HTMLElement) this.#intercept(event, form, button);
  };

  #intercept(event, form, button) {
    event.preventDefault();
    event.stopImmediatePropagation();
    if (this.#pending || document.querySelector('.pplr_atc_loading, .pplr_adding, #p_loading-spinner')) return;
    this.#validateAdd(form, button);
  }

  /** Reuse the form live region to avoid duplicate announcements. */
  #message(form, message) {
    const owner = form.closest('product-form-component');
    let visual = form.querySelector('[data-fehma-stock-message]');
    if (!visual) {
      visual = document.createElement('span');
      visual.dataset.fehmaStockMessage = '';
      visual.className = 'product-form-text__error';
      form.querySelector('.product-form-buttons')?.prepend(visual);
    }
    visual.textContent = message;
    visual.classList.toggle('hidden', !message);
    const live = owner?.querySelector('[ref="liveRegion"]');
    if (live) live.textContent = message;
    if (message && message !== this.dataset.checking) {
      // A sticky-button error must be visible as well as announced.
      visual.scrollIntoView({ block: 'center', behavior: 'instant' });
    }
  }

  async #read(url, options, signal) {
    const request = new AbortController();
    const abort = () => request.abort();
    signal.addEventListener('abort', abort, { once: true });
    if (signal.aborted) request.abort();
    const timeout = setTimeout(abort, 10000);
    let text;
    try {
      const response = await fetch(url, { ...options, signal: request.signal, cache: 'no-store' });
      if (!response.ok) throw new Error('Stock lookup failed');
      text = await response.text();
    } finally {
      clearTimeout(timeout);
      signal.removeEventListener('abort', abort);
    }
    return text;
  }

  async #snapshot(path, signal) {
    const url = new URL(path, window.location.origin);
    if (url.origin !== window.location.origin) throw new Error('Unexpected inventory origin');
    url.searchParams.set('section_id', 'fehma-stock-data');
    url.searchParams.set('_fehma_stock', String(Date.now()));
    const text = await this.#read(url, { credentials: 'same-origin' }, signal);
    const html = new DOMParser().parseFromString(text, 'text/html');
    const raw = html.querySelector('[data-fehma-stock-snapshot]')?.getAttribute('data-json');
    if (!raw) throw new Error('Stock response missing');
    const snapshot = JSON.parse(raw);
    if (snapshot.theme_id !== THEME_ID || !Array.isArray(snapshot.cart?.items)) throw new Error('Wrong theme response');
    // Shopify Liquid serialises line-item properties as [name, value] pairs.
    for (const item of snapshot.cart.items) {
      if (Array.isArray(item.properties)) item.properties = Object.fromEntries(item.properties);
    }
    return snapshot;
  }

  /** Zepto uses this same read-only endpoint for variants hidden from product lookup. */
  async #inventory(ids, names, signal) {
    const body = new URLSearchParams({ shop: window.Shopify.shop });
    for (const id of ids) body.append('vdata[]', id);
    const url = new URL(INVENTORY_URL);
    url.searchParams.set('theme_id', String(THEME_ID));
    url.searchParams.set('_fehma_stock', String(Date.now()));
    const text = await this.#read(url, {
      method: 'POST', credentials: 'omit',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' }, body: body.toString(),
    }, signal);
    const products = JSON.parse(text);
    if (!Array.isArray(products)) throw new Error('Unknown inventory response');
    const inventory = new Map();
    for (const product of products) {
      for (const variant of product.variants || []) {
        const id = String(variant.id);
        if (!ids.includes(id)) continue;
        if (!Object.hasOwn(variant, 'inventory_management')) throw new Error('Inventory tracking metadata missing');
        const management = variant.inventory_management;
        const policy = String(variant.inventory_policy || '').toLowerCase();
        const quantity = management ? Number(variant.inventory_quantity) : null;
        if (management && (variant.inventory_quantity == null || !Number.isSafeInteger(quantity))) {
          throw new Error('Unknown inventory quantity');
        }
        inventory.set(id, {
          id, name: names.get(id), inventory_management: management,
          inventory_policy: policy, inventory_quantity: quantity,
          available: String(product.status).toLowerCase() === 'active' && (!management || policy === 'continue' || quantity > 0),
        });
      }
    }
    return inventory;
  }

  #cartPath() {
    return `${window.Shopify.routes.root}cart`;
  }

  #stockMessage(unavailable) {
    if (unavailable.some((variant) => !variant.name)) throw new Error('Unavailable charm label missing');
    const names = [...new Set(unavailable.map((variant) => variant.name))].join(', ');
    return this.dataset.insufficient.replace('[charm]', names);
  }

  async #validateAdd(form, button) {
    const lock = {};
    this.#pending = lock;
    const signal = this.#controller.signal;
    form.setAttribute('aria-busy', 'true');
    this.#message(form, this.dataset.checking);
    try {
      const selection = this.#selection(form);
      if (!form.checkValidity()) {
        this.#message(form, '');
        form.reportValidity();
        return;
      }
      const before = await this.#snapshot(this.#cartPath(), signal);
      const required = new Map();
      const names = new Map();
      for (const link of selection.links) {
        names.set(link.id, link.name);
        required.set(link.id, (required.get(link.id) || 0) + selection.quantity);
      }
      const inventory = required.size ? await this.#inventory([...required.keys()], names, signal) : new Map();
      const cart = await this.#snapshot(this.#cartPath(), signal);
      if (cartSignature(before.cart.items) !== cartSignature(cart.cart.items) ||
          selection.signature !== this.#selection(form).signature || !form.isConnected || !button.isConnected) {
        this.#message(form, this.dataset.changed);
        return;
      }
      const unavailable = shortages(cartRequirements(cart.cart.items), required, inventory);
      if (unavailable.length) {
        this.#message(form, this.#stockMessage(unavailable));
        return;
      }
      let labels = form.querySelector('input[name="properties[_fehma_inventory_labels]"]');
      if (!labels) {
        labels = document.createElement('input');
        labels.type = 'hidden';
        labels.name = 'properties[_fehma_inventory_labels]';
        form.append(labels);
      }
      labels.value = JSON.stringify(Object.fromEntries(names));
      this.#message(form, '');
      // This synchronous one-event permit cannot authorise a later user click.
      this.#replaying = button;
      try { button.click(); } finally { this.#replaying = null; }
    } catch (error) {
      if (!signal.aborted) {
        this.#message(form, this.dataset.unverified);
        console.warn('[FEHMA stock] Validation stopped:', error);
      }
    } finally {
      form.removeAttribute('aria-busy');
      if (this.#pending === lock) this.#pending = null;
    }
  }

  /**
   * Validate an increase by current line key. The caller releases the lock when
   * its mutation settles. Missing component metadata fails without a cart write.
   * @param {string} key @param {number} quantity
   * @returns {Promise<{allowed: boolean, message?: string, line?: number, release?: () => void}>}
   */
  async validateCartIncrease(key, quantity) {
    if (this.#pending || document.querySelector('.pplr_atc_loading, .pplr_adding, #p_loading-spinner')) {
      return { allowed: false, message: this.dataset.changed };
    }
    const lock = {};
    this.#pending = lock;
    const release = () => { if (this.#pending === lock) this.#pending = null; };
    try {
      const signal = this.#controller.signal;
      const before = await this.#snapshot(this.#cartPath(), signal);
      const index = before.cart.items.findIndex((item) => item.key === key);
      if (index < 0) throw new Error('Cart line changed');
      const item = before.cart.items[index];
      if (quantity <= item.quantity || !item.properties?._pplr_cart_variant) {
        release();
        return { allowed: true, line: index + 1 };
      }
      const required = lineRequirements(item);
      const names = new Map([item.variant, ...item.components].map((variant) => [String(variant.id), variant.name]));
      const labels = parseInventoryProperty(item.properties._fehma_inventory_labels || '{}');
      for (const [id, name] of Object.entries(labels)) {
        if (typeof name === 'string' && name) names.set(id, name);
      }
      const inventory = await this.#inventory([...required.keys()], names, signal);
      const after = await this.#snapshot(this.#cartPath(), signal);
      if (cartSignature(before.cart.items) !== cartSignature(after.cart.items)) throw new Error('Cart changed');
      for (const [id, count] of required) required.set(id, count * (positiveInteger(quantity) - item.quantity));
      const unavailable = shortages(cartRequirements(after.cart.items), required, inventory);
      if (unavailable.length) {
        release();
        return { allowed: false, message: this.#stockMessage(unavailable) };
      }
      return { allowed: true, line: index + 1, release };
    } catch (error) {
      release();
      console.warn('[FEHMA stock] Cart validation stopped:', error);
      return { allowed: false, message: this.dataset.unverified };
    }
  }
}

if (!customElements.get('fehma-stock-validation')) {
  customElements.define('fehma-stock-validation', FehmaStockValidation);
}
